/**
 * Medplum Questionnaire search helpers for Template Library category tabs.
 */
import type { MedplumClient } from '@medplum/core';
import type { Bundle, Questionnaire } from '@medplum/fhirtypes';
import type { TemplateCategory } from '../types';
import { getTemplateCategory, templateCategorySearchTagValues } from '../utils/templateTags';

export function dedupeQuestionnaires(questionnaires: Questionnaire[]): Questionnaire[] {
  const seen = new Set<string>();
  const out: Questionnaire[] = [];
  for (const q of questionnaires) {
    const key = q.id ?? q.url ?? q.name;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(q);
  }
  return out;
}

/** Search by canonical, legacy, and document-category tags that map to the tab. */
export async function searchQuestionnairesByCategory(
  medplum: MedplumClient,
  category: TemplateCategory,
  params: Record<string, string>,
): Promise<Questionnaire[]> {
  const tagValues = templateCategorySearchTagValues(category);
  const bundles = await Promise.all(
    tagValues.map((tag) => medplum.search('Questionnaire', { ...params, _tag: tag })),
  );
  const merged = bundles.flatMap((bundle: Bundle) =>
    (bundle.entry ?? [])
      .map((entry) => entry.resource as Questionnaire)
      .filter((resource): resource is Questionnaire => Boolean(resource)),
  );
  return dedupeQuestionnaires(merged).filter((q) => getTemplateCategory(q) === category);
}
